import React, { FC, useContext, useEffect, useState } from 'react'
import { PageTitle } from '@components/configs/PageTitle'
import SectionTitle from '@components/ui/sectionTitle'
import { AppStateContext } from '@components/layouts/DynamicLayout'
import { formatStrapiText } from '@utils/methods'
import * as pageUtils from '@pages/auth/requests/__index.utils'
import { Dictionary } from 'typings/shared'
import RequestCard from '@components/ui/requestCard'
import Notification from '@components/ui/notification'
import { joinClasses } from '@utils/styles'
import ReactPaginate from '@components/ui/pagination'
import Loader from '@components/ui/loader'
import { Request } from '@services/models'
import { CustomDate } from '@services/models/shared.model'
import { pushEvent } from '@utils/tracking'
import { LanguageEnum } from '@services/constants'
import { buildMyRequestsSearchFiltersPayload } from '@utils/searchTracking'

// File: Requests page
// Purpose: display a paginated, filterable, and sortable list of the authenticated user's requests.
// Note: component handles data fetching, filtering, sorting and UI — consider extracting fetch and filter logic for SRP.

// Filter / sort constants used by UI and to map to backend codes.
const FILTER_CONSTANTS = {
    TAB: {
        CLOSED: 'closed',
        ACTIVE: 'active'
    },
    SORT: {
        CREATION_DATE: 'dateAdd',
        MODIFICATION_DATE: 'dateUpdate',
        CREATION_DATE_ASC: 'dateAdd_asc',
        CREATION_DATE_DESC: 'dateAdd_desc',
        MODIFICATION_DATE_ASC: 'dateUpdate_asc',
        MODIFICATION_DATE_DESC: 'dateUpdate_desc'
    }
} as const

// Map sorter keys (UI) -> backend numeric codes used by authUser.fetchRequests.
// Suggestion: keep this mapping centralized (or export it) to avoid duplicated logic elsewhere.
const SORT_CODE_MAP: Record<string, number> = {
    [FILTER_CONSTANTS.SORT.CREATION_DATE_DESC]: 0,
    [FILTER_CONSTANTS.SORT.CREATION_DATE_ASC]: 1,
    [FILTER_CONSTANTS.SORT.MODIFICATION_DATE_DESC]: 2,
    [FILTER_CONSTANTS.SORT.MODIFICATION_DATE_ASC]: 3
}

enum REQUEST_STATUS {
    ALL = 0,
    OPEN = 1,
    PROCESSING = 2,
    AWAITING_PAYMENT = 3,
    AWAITING_FILE = 4
}

enum ORDER {
    ASC = 'ASC',
    DESC = 'DESC'
}

const Requests: FC = () => {
    const { pageData, authUser } = useContext(AppStateContext)

    // State: raw server results, loading & UI controls
    const [requests, setRequests] = useState<Request[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [filter, setFilter] = useState<REQUEST_STATUS>(REQUEST_STATUS.ALL)
    const [sorter, setSorter] = useState<string>(
        FILTER_CONSTANTS.SORT.CREATION_DATE_DESC
    )
    const [tabFilter, setTabFilter] = useState<string>(
        FILTER_CONSTANTS.TAB.ACTIVE
    )
    // tempRequests holds filtered/sorted client-side view (separate from server results)
    const [tempRequests, setTempRequests] = useState<Request[]>([])
    const [isInformationSent, setIsInformationSent] = useState<boolean>(false)
    const [requestCode, setRequestCode] = useState<Request['code']>()
    const [pageCount, setPageCount] = useState(0)
    const [activePage, setActivePage] = useState(0)
    const [shouldTrackFilteredSearch, setShouldTrackFilteredSearch] =
        useState<boolean>(false)

    const getTrackingFiltersPayload = () => {
        return buildMyRequestsSearchFiltersPayload(
            tabFilter,
            Number(filter),
            sorter,
            pageData?.allAssets?.[LanguageEnum.FR]
        )
    }

    // Fetch requests from backend. Responsibility: ask authUser for data.
    // Suggestion: extract to useRequests hook / service for testability & SRP.
    const fetchRequests = async (
        newPage?: number,
        newTabFilter?: string,
        filterValue?: number,
        sorterValue?: number,
    ) => {
        if (authUser) {
            try {
                // backend expects tab: 2 for closed, 1 for active
                const { requests, totalPages } =
                    await authUser.fetchRequests(
                        false,
                        newPage ?? 1,
                        newTabFilter === FILTER_CONSTANTS.TAB.CLOSED ? 2 : 1,
                        filterValue,
                        sorterValue,
                        true
                    )

                setPageCount(totalPages)
                setRequests(requests)
            } catch (error: any) {
                console.error(error?.message)
            } finally {
                // small UX delay to avoid flicker
                setTimeout(() => setIsLoading(false), 200)
            }
        }
    }

    useEffect(() => {
        // initial load when authUser available
        fetchRequests().catch(console.error)
    }, [authUser])

    const onConsentValidated = (consentId) => {
        if (consentId) {
            setIsInformationSent(true)
        }
    }

    // Items renderer for current page
    const Items = ({ currentItems }) => {
        return (
            <div className="items">
                {!!currentItems &&
                    currentItems.map((request, key) => (
                        <RequestCard
                            request={request}
                            pageAssets={pageData?.assets}
                            requestTypes={pageData.requests}
                            key={key}
                            onConsentValidated={onConsentValidated}
                            setRequestCode={setRequestCode}
                            data-testid="requestcard"
                        />
                    ))}
            </div>
        )
    }

    const PaginatedItems = () => {
        // handle page change -> fetch from server
        const handlePageClick = (event) => {
            setIsLoading(true)
            setRequests([])
            setActivePage(event.selected)
            // Map UI sorter key to backend numeric code using SORT_CODE_MAP
            fetchRequests(
                event.selected + 1,
                tabFilter,
                filter,
                SORT_CODE_MAP[sorter] ?? 0,
            ).catch(console.error)
        }

        return (
            <>
                {isLoading ? (
                    <Loader text={pageData?.assets?.loading} />
                ) : (
                    <Items currentItems={requests} />
                )}

                <ReactPaginate
                    hide={pageCount <= 1}
                    forcePage={activePage}
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={pageCount}
                    previousLabel="&nbsp;"
                    nextLabel="&nbsp;"
                    pageClassName={pageUtils.classes.pager}
                    pageLinkClassName={pageUtils.classes.pager}
                    previousClassName={pageUtils.classes.pager}
                    previousLinkClassName={joinClasses([
                        pageUtils.classes.pager,
                        pageUtils.classes.simple,
                        pageUtils.classes.previous
                    ])}
                    nextClassName={pageUtils.classes.pager}
                    nextLinkClassName={joinClasses([
                        pageUtils.classes.pager,
                        pageUtils.classes.simple
                    ])}
                    breakLabel="..."
                    breakClassName={pageUtils.classes.pager}
                    breakLinkClassName={pageUtils.classes.pager}
                    containerClassName={pageUtils.classes.pagination}
                    activeClassName={pageUtils.classes.currentPage}
                    firstItemLabel="&nbsp;"
                    lastItemLabel="&nbsp;"
                    firstItemClassName={pageUtils.classes.pager}
                    lastItemClassName={pageUtils.classes.pager}
                    firstItemLinkClassName={joinClasses([
                        pageUtils.classes.pager,
                        pageUtils.classes.double,
                        pageUtils.classes.previous
                    ])}
                    lastItemLinkClassName={joinClasses([
                        pageUtils.classes.pager,
                        pageUtils.classes.double
                    ])}
                    disabledClassName={pageUtils.classes.hidde}
                />
            </>
        )
    }

    // UI section: filters (tab, status filter, sort)
    const displayFilter = (pageAssets: Dictionary) => (
        <>
            <div
                className={joinClasses([
                    pageUtils.classes.filtersSection,
                    `${
                        filter === REQUEST_STATUS.ALL && !requests.length
                            ? pageUtils.classes.hidde
                            : ''
                    }`
                ])}
            >
                {tabFilter == FILTER_CONSTANTS.TAB.ACTIVE && (
                    <div
                        className={pageUtils.classes.filters}
                        style={{ marginRight: 30 }}
                    >
                        <label style={{ whiteSpace: 'nowrap' }}>
                            <b>{formatStrapiText(pageAssets?.filter_label)}</b>
                        </label>
                        <select
                            className={joinClasses([
                                pageUtils.classes.inline,
                                pageUtils.classes.select,
                                'filter'
                            ])}
                            onChange={handleChange}
                            defaultValue={REQUEST_STATUS.ALL}
                            value={filter}
                        >
                            <option value={REQUEST_STATUS.ALL}>
                                {formatStrapiText(
                                    pageAssets?.page_myRequest_select_allStatuses
                                )}
                            </option>
                            <option value={REQUEST_STATUS.OPEN}>
                                {formatStrapiText(pageAssets?.page_myRequest_select_open)}
                            </option>
                            <option value={REQUEST_STATUS.PROCESSING}>
                                {formatStrapiText(pageAssets?.page_myRequest_select_processing)}
                            </option>
                            <option value={REQUEST_STATUS.AWAITING_PAYMENT}>
                                {formatStrapiText(pageAssets?.page_myRequest_select_payment)}
                            </option>
                            <option value={REQUEST_STATUS.AWAITING_FILE}>
                                {formatStrapiText(pageAssets?.page_myRequest_select_awaiting_file)}
                            </option>
                        </select>
                    </div>
                )}
                <div className={pageUtils.classes.filters}>
                    <label style={{ whiteSpace: 'nowrap' }}>
                        <b>{formatStrapiText(pageAssets?.sort_label)}</b>
                    </label>
                    <select
                        style={{ width: '100%' }}
                        className={joinClasses([
                            pageUtils.classes.inline,
                            pageUtils.classes.select,
                            'sort'
                        ])}
                        onChange={handleChange}
                        defaultValue={FILTER_CONSTANTS.SORT.CREATION_DATE_DESC}
                    >
                        <option value={FILTER_CONSTANTS.SORT.CREATION_DATE_DESC}>
                            {formatStrapiText(
                                pageAssets?.page_myRequest_select_creationDate_desc
                            )}
                        </option>
                        <option value={FILTER_CONSTANTS.SORT.CREATION_DATE_ASC}>
                            {formatStrapiText(
                                pageAssets?.page_myRequest_select_creationDate_asc
                            )}
                        </option>
                        <option value={FILTER_CONSTANTS.SORT.MODIFICATION_DATE_DESC}>
                            {formatStrapiText(
                                pageAssets?.page_myRequest_select_lastModificationDate_desc
                            )}
                        </option>
                        <option value={FILTER_CONSTANTS.SORT.MODIFICATION_DATE_ASC}>
                            {formatStrapiText(
                                pageAssets?.page_myRequest_select_lastModificationDate_asc
                            )}
                        </option>
                    </select>
                </div>
            </div>
            {!requests.length && !isLoading && filter !== REQUEST_STATUS.ALL && (
                <Notification
                    hasHtml
                    type="info"
                    text={
                        pageData.assets?.request_emptyFilter +
                        ' <b>' +
                        `${getActiveFilterText(pageData.assets)}` +
                        '</b>.'
                    }
                />
            )}
        </>
    )

    // Helper: translate current numeric filter into localized text for notifications
    const getActiveFilterText = (pageAssets: Dictionary) => {
        if (tabFilter === FILTER_CONSTANTS.TAB.ACTIVE) {
            switch (Number(filter)) {
                case REQUEST_STATUS.ALL:
                    return formatStrapiText(pageAssets?.page_myRequest_select_allStatuses)
                case REQUEST_STATUS.OPEN:
                    return formatStrapiText(pageAssets?.page_myRequest_select_open)
                case REQUEST_STATUS.PROCESSING:
                    return pageAssets?.page_myRequest_select_processing
                case REQUEST_STATUS.AWAITING_PAYMENT:
                    return formatStrapiText(pageAssets?.page_myRequest_select_payment)
                case REQUEST_STATUS.AWAITING_FILE:
                    return formatStrapiText(pageAssets?.page_myRequest_select_awaiting_file)
            }
        } else if (tabFilter === FILTER_CONSTANTS.TAB.CLOSED) {
            return formatStrapiText(pageAssets?.page_myRequest_select_closed)
        }
    }

    // Tab switcher UI
    const displayTabFilter = (pageAssets: Dictionary) => {
        return (
            <div className={pageUtils.classes.filterTab}>
                <div
                    className={joinClasses([
                        tabFilter == FILTER_CONSTANTS.TAB.ACTIVE ? 'active' : ''
                    ])}
                    onClick={() => handleTabFilterChange(FILTER_CONSTANTS.TAB.ACTIVE)}
                >
                    {formatStrapiText(pageAssets?.page_myRequest_tabFilter_active)}
                </div>
                <div
                    className={joinClasses([
                        tabFilter == FILTER_CONSTANTS.TAB.CLOSED ? 'active' : ''
                    ])}
                    onClick={() => handleTabFilterChange(FILTER_CONSTANTS.TAB.CLOSED)}
                >
                    {formatStrapiText(pageAssets?.page_myRequest_tabFilter_closed)}
                </div>
            </div>
        )
    }

    // Reset UI consts and refetch page 1 with new filters/sort
    const resetConstsOnFetch = (newTabFilter, newFilter, newSorter) => {
        setIsLoading(true)
        setRequests([])
        setActivePage(0)
        fetchRequests(
            1,
            newTabFilter,
            newFilter,
            SORT_CODE_MAP[newSorter] ?? 0
        ).catch(console.error)
    }

    // Handler for both filter and sort selects.
    // Note: it uses className.includes to know which select changed — brittle.
    // Suggestion: use separate handlers or data-* attributes for clarity.
const handleChange = (e) => {
        let tmpFilter = filter
        let tmpSorter = sorter

        if (e.target.className.includes('filter')) {
            setFilter(e.target.value)
            tmpFilter = e.target.value
        } else if (e.target.className.includes('sort')) {
            setSorter(e.target.value)
            tmpSorter = e.target.value
        }

        setShouldTrackFilteredSearch(true)
        resetConstsOnFetch(tabFilter, tmpFilter, tmpSorter)
    }

    // Switch tab (ACTIVE / CLOSED) and reset status filter
    const handleTabFilterChange = (value) => {
        if (value !== tabFilter) {
            setTabFilter(value)
            setFilter(REQUEST_STATUS.ALL)
            setShouldTrackFilteredSearch(true)
            resetConstsOnFetch(value, REQUEST_STATUS.ALL, sorter)
        }
    }

    // Client-side filtering pipeline applied to the last fetched requests.
    // Responsibilities: filter out excluded types, split by tab, apply status filter and determine order.
    // Suggestion: extract into pure utility function for testability.
    const applyFilter = (
        tabFilter: string,
        filter: REQUEST_STATUS
    ) => {
        let value = [...requests] // operate on a shallow copy to avoid mutating original state
        let order: ORDER = ORDER.DESC

        // hide specific request types (business rule)
        //value =
        //    value.filter(
        //        (request) =>
        //            ![
        //                `${IDENTIFIERS_OF_TN_REQUEST_TYPE.OIL_HEATING_DECLARATION_FORM}`,
        //                `${IDENTIFIERS_OF_TN_REQUEST_TYPE.FIREPLACE_DECLARATION}`,
        //                `${IDENTIFIERS_OF_TN_REQUEST_TYPE.SELF_RELIEF_WATER_COUNTER_FORM}`
        //            ].includes(request.typeId)
        //    ) || []

        // tabFilter (ACTIVE or CLOSED)
        value =
            value.filter((request) => {
                if (tabFilter == FILTER_CONSTANTS.TAB.CLOSED) {
                    return request.status == (FILTER_CONSTANTS.TAB.CLOSED as string)
                } else {
                    return request.status !== (FILTER_CONSTANTS.TAB.CLOSED as string)
                }
            }) || []

        // when ACTIVE + specific status selected, filter by numeric status and exclude cancelled
        if (
            tabFilter == FILTER_CONSTANTS.TAB.ACTIVE &&
            filter !== REQUEST_STATUS.ALL
        ) {
            value =
                value.filter((request) => {
                    return (
                        request.status.toString() === filter.toString() &&
                        !request.cancelReason
                    )
                }) || []
        }

        // determine order based on sorter key
        if (
            sorter === FILTER_CONSTANTS.SORT.CREATION_DATE_ASC ||
            sorter === FILTER_CONSTANTS.SORT.MODIFICATION_DATE_ASC
        ) {
            order = ORDER.ASC
        } else if (
            sorter === FILTER_CONSTANTS.SORT.CREATION_DATE_DESC ||
            sorter === FILTER_CONSTANTS.SORT.MODIFICATION_DATE_DESC
        ) {
            order = ORDER.DESC
        }

        sortRequests(value, sorter, order)
    }

    // Sorting helper: currently supports date-based fields.
    // Caution: Array.sort mutates the array; setTempRequests receives the mutated array.
    // Suggestion: use a sorted copy [...requests].sort(...) to be explicit.
    const sortRequests = (
        requests: Request[],
        sorter: string,
        order: ORDER = ORDER.DESC
    ) => {
        const datesSort: Set<string> = new Set([
            FILTER_CONSTANTS.SORT.CREATION_DATE_ASC,
            FILTER_CONSTANTS.SORT.MODIFICATION_DATE_ASC,
            FILTER_CONSTANTS.SORT.CREATION_DATE_DESC,
            FILTER_CONSTANTS.SORT.MODIFICATION_DATE_DESC
        ])

        if (datesSort.has(sorter)) {
            const temp_sorter = sorter?.split('_')[0] // 'dateAdd' or 'dateUpdate' expected

            if (order === ORDER.ASC) {
                setTempRequests(
                    requests.sort((a, b) =>
                        CustomDate.diff(a[temp_sorter], b[temp_sorter])
                    )
                )
            } else {
                setTempRequests(
                    requests.sort((a, b) =>
                        CustomDate.diff(b[temp_sorter], a[temp_sorter])
                    )
                )
            }

            return
        }
    }

    // keep tempRequests up-to-date when dependencies change
    useEffect(() => {
        applyFilter(tabFilter, filter)
    }, [tabFilter, filter, requests, sorter])

    useEffect(() => {
        if (!shouldTrackFilteredSearch || isLoading) {
            return
        }

        pushEvent('search_results', {
            search_term: '',
            results_count: requests.length,
            search_location: 'my_requests_filters',
            search_type: 'global',
            search_filters: getTrackingFiltersPayload()
        })

        setShouldTrackFilteredSearch(false)
    }, [
        shouldTrackFilteredSearch,
        isLoading,
        requests,
        tabFilter,
        filter,
        sorter
    ])

    return (
        <>
            <PageTitle title={formatStrapiText(pageData?.title)} />
            <SectionTitle title={formatStrapiText(pageData?.title)} />
            {displayTabFilter(pageData?.assets)}
            <section className={pageUtils.classes.section}>
                {isInformationSent && (
                    <Notification
                        showExitBtn
                        text={`${pageData.assets?.myRequest_information_well_added_to_the_request} <b>${requestCode}</b>`}
                        type="success"
                        hasHtml
                        onClickCancelBtn={() => {
                            setIsInformationSent(false)
                        }}
                    />
                )}

                {requests.length <= 0 &&
                    !isLoading &&
                    filter === REQUEST_STATUS.ALL && (
                        <Notification
                            type="info"
                            text={pageData?.assets?.request_notificationEmptyMessage}
                        />
                    )}

                <>
                    {displayFilter(pageData?.assets)}
                    <PaginatedItems />
                </>
            </section>
        </>
    )
}

export default Requests
